import { useEffect, useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import EmptyState from '@/components/admin/EmptyState';
import { adminApi } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Trash2, Search, ExternalLink, Phone, Mail, Calendar, Users } from 'lucide-react';
import { RESERVATION_STATUS, RESERVATION_STATUS_COLORS, RESERVATION_TYPES } from '@/lib/data';
import { toast } from 'sonner';
import { format } from 'date-fns';

const niceDate = (iso) => { try { return format(new Date(iso), 'dd/MM/yyyy HH:mm'); } catch { return iso?.slice(0, 16); } };
const typeLabel = (k) => RESERVATION_TYPES.find((t) => t.value === k)?.label || k;

export default function AdminReservationsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [openId, setOpenId] = useState(null);
    const [newNote, setNewNote] = useState('');

    const load = () => {
        setLoading(true);
        const params = {};
        if (statusFilter !== 'all') params.status = statusFilter;
        if (typeFilter !== 'all') params.reservation_type = typeFilter;
        if (q) params.q = q;
        adminApi.reservations.list(params).then(setItems).finally(() => setLoading(false));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const current = items.find((r) => r.id === openId);

    const changeStatus = async (id, status) => { await adminApi.reservations.update(id, { status }); toast.success('Estado actualizado'); load(); };
    const addNote = async (id) => { if (!newNote.trim()) return; await adminApi.reservations.addNote(id, newNote.trim()); setNewNote(''); const r = await adminApi.reservations.get(id); setItems((prev) => prev.map((x) => (x.id === id ? r : x))); };
    const remove = async (id) => { await adminApi.reservations.delete(id); toast.success('Reserva eliminada'); setOpenId(null); load(); };

    return (
        <AdminLayout title="Reservas">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                    <Input data-testid="admin-reservations-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="Buscar por nombre, teléfono, email…" className="pl-9" />
                </div>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setTimeout(load, 0); }}>
                    <SelectTrigger className="w-full sm:w-48" data-testid="admin-reservations-status-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos los estados</SelectItem>
                        {Object.entries(RESERVATION_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setTimeout(load, 0); }}>
                    <SelectTrigger className="w-full sm:w-48" data-testid="admin-reservations-type-filter"><SelectValue placeholder="Tipo" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos los tipos</SelectItem>
                        {RESERVATION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
            {loading ? (
                <div className="rounded-2xl bg-white border border-border p-10 animate-pulse h-64" />
            ) : items.length === 0 ? (
                <EmptyState title="Sin reservas aún" description="Cuando alguien complete el formulario de Reserva Online aparecerá aquí." />
            ) : (
                <div className="rounded-2xl bg-white border border-border shadow-soft overflow-x-auto">
                    <table data-testid="admin-reservations-table" className="min-w-full text-sm">
                        <thead className="bg-secondary/60 text-xs uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="text-left px-4 py-3">Nombre</th>
                                <th className="text-left px-4 py-3">Contacto</th>
                                <th className="text-left px-4 py-3">Tipo</th>
                                <th className="text-left px-4 py-3">Fecha evento</th>
                                <th className="text-left px-4 py-3">Personas</th>
                                <th className="text-left px-4 py-3">Estado</th>
                                <th className="text-right px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {items.map((r) => (
                                <tr key={r.id} className="hover:bg-secondary/40">
                                    <td className="px-4 py-3"><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground">{niceDate(r.created_at)}</div></td>
                                    <td className="px-4 py-3 text-xs">{r.phone && <div className="flex items-center gap-1"><Phone className="size-3" /> {r.phone}</div>}{r.email && <div className="flex items-center gap-1"><Mail className="size-3" /> {r.email}</div>}</td>
                                    <td className="px-4 py-3 text-xs">{typeLabel(r.reservation_type)}</td>
                                    <td className="px-4 py-3 text-xs whitespace-nowrap">{r.event_date || '—'}</td>
                                    <td className="px-4 py-3 text-xs">{r.people_count ? <span className="inline-flex items-center gap-1"><Users className="size-3" /> {r.people_count}</span> : '—'}</td>
                                    <td className="px-4 py-3"><span className={`text-[11px] px-2 py-1 rounded-full border inline-block ${RESERVATION_STATUS_COLORS[r.status] || ''}`}>{RESERVATION_STATUS[r.status] || r.status}</span></td>
                                    <td className="px-4 py-3 text-right"><button onClick={() => setOpenId(r.id)} data-testid={`reservation-row-open-${r.id}`} className="inline-flex items-center gap-1 text-xs text-[hsl(var(--kche-pink))] hover:underline">Ver <ExternalLink className="size-3.5" /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
                <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
                    {current && (
                        <>
                            <SheetHeader>
                                <SheetTitle>{current.name}</SheetTitle>
                                <SheetDescription>Detalle de la reserva</SheetDescription>
                            </SheetHeader>
                            <div className="mt-4 grid gap-3 text-sm">
                                <div className="grid gap-1">
                                    {current.phone && <div className="flex items-center gap-2"><Phone className="size-4" /> {current.phone}</div>}
                                    {current.email && <div className="flex items-center gap-2"><Mail className="size-4" /> {current.email}</div>}
                                    {current.event_date && <div className="flex items-center gap-2"><Calendar className="size-4" /> {current.event_date}</div>}
                                    {current.people_count ? <div className="flex items-center gap-2"><Users className="size-4" /> {current.people_count} personas</div> : null}
                                    <div className="text-xs text-muted-foreground">Recibido: {niceDate(current.created_at)}</div>
                                    <div className="text-xs text-muted-foreground">Origen: {current.source_page || '—'}</div>
                                </div>
                                <div><Label>Tipo</Label><p className="mt-1">{typeLabel(current.reservation_type)}</p></div>
                                <div><Label>Canal preferido</Label><p className="mt-1">{current.preferred_channel}</p></div>
                                <div><Label>Mensaje</Label><p className="mt-1 whitespace-pre-wrap">{current.message || '—'}</p></div>
                                <div>
                                    <Label>Estado</Label>
                                    <Select value={current.status} onValueChange={(v) => changeStatus(current.id, v)}>
                                        <SelectTrigger className="mt-1" data-testid="reservation-status-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(RESERVATION_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="border-t border-border pt-3">
                                    <h4 className="font-display font-semibold">Notas internas</h4>
                                    <ul className="mt-2 space-y-2">
                                        {(current.notes || []).map((n) => (
                                            <li key={n.id} className="rounded-lg bg-secondary p-2"><p className="text-sm whitespace-pre-wrap">{n.text}</p><p className="text-[10px] text-muted-foreground mt-1">{niceDate(n.created_at)} · {n.author}</p></li>
                                        ))}
                                        {(current.notes || []).length === 0 && <li className="text-xs text-muted-foreground">Sin notas aún.</li>}
                                    </ul>
                                    <Textarea data-testid="reservation-note-input" rows={2} value={newNote} onChange={(e) => setNewNote(e.target.value)} className="mt-2" placeholder="Añadir nota interna…" />
                                    <button onClick={() => addNote(current.id)} data-testid="reservation-note-add" className="mt-2 inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-3 py-1.5 text-sm">Añadir nota</button>
                                </div>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <button className="mt-4 inline-flex items-center gap-2 rounded-lg bg-destructive text-destructive-foreground px-3 py-2 text-sm" data-testid="reservation-delete-button"><Trash2 className="size-4" /> Eliminar reserva</button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>¿Eliminar esta reserva?</AlertDialogTitle>
                                            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                            <AlertDialogAction onClick={() => remove(current.id)}>Eliminar</AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        </>
                    )}
                </SheetContent>
            </Sheet>
        </AdminLayout>
    );
}
